import { useEffect, useRef } from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import autosize from 'autosize';
import { format, isValid } from 'date-fns';
import { TopicNoteComponentProps } from './TopicNote.container';
import * as Styles from './TopicNote.styles';

const TopicNoteComponent = function ({
  isEditing,
  onSaveNote,
  author,
  isNewInput,
  createdAt,
  text,
  onEditNote,
  onUpdateNote,
  onDeleteNote,
  onCancelEditingNote,
  isSubmiting,
}: TopicNoteComponentProps): JSX.Element {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const isTextEmpty = !text?.replace(/\s/g, '');
  const areButtonDisabled = isTextEmpty || isSubmiting;

  useEffect(() => {
    autosize(textareaRef.current);
  }, [isEditing, isSubmiting]);

  useEffect(() => {
    if (isEditing && textareaRef.current && !isNewInput) {
      textareaRef.current.focus();
    }
  }, [isEditing, isNewInput, isSubmiting]);

  const dateFormat = `MMM dd hh:mm aaaaa'm'`;
  const isDateValid = createdAt && isValid(new Date(createdAt));
  const formattedDate = isDateValid && format(new Date(createdAt), dateFormat);
  return (
    <Styles.Note>
      <Styles.NoteContent>
        <Styles.UserAvatar firstName="Zion" lastName="Admin" />
        <Styles.CardAuthor>{author?.name}</Styles.CardAuthor>
        {formattedDate && <Styles.PublishedAt>{formattedDate}</Styles.PublishedAt>}
      </Styles.NoteContent>
      <div>
        {(isNewInput || isEditing) && !isSubmiting ? (
          <Styles.TextArea
            ref={textareaRef}
            onChange={onUpdateNote}
            value={text}
            rows={1}
            placeholder="Add a note"
          />
        ) : (
          <Styles.NoteContentText>{text}</Styles.NoteContentText>
        )}
      </div>
      <Styles.CardFooter>
        {((isNewInput && !isTextEmpty) || isEditing) && (
          <>
            <Styles.ActionButton
              disabled={areButtonDisabled}
              onClick={() => onSaveNote()}
              isSaveButton
              type="button"
            >
              {isNewInput ? <SendIcon /> : <SaveIcon />}
              {isNewInput ? 'Publish' : 'Save'}
            </Styles.ActionButton>
            <Styles.ActionButton
              onClick={() => onCancelEditingNote()}
              withDangerAlert
              type="button"
            >
              <CancelIcon />
              Cancel
            </Styles.ActionButton>
          </>
        )}

        {!isEditing && !isNewInput && (
          <>
            <Styles.ActionButton type="button" onClick={() => onEditNote()}>
              <ModeEditIcon />
              Edit
            </Styles.ActionButton>
            <Styles.ActionButton
              disabled={isSubmiting}
              withDangerAlert
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onDeleteNote();
              }}
            >
              <DeleteIcon />
              Delete
            </Styles.ActionButton>
          </>
        )}
      </Styles.CardFooter>
    </Styles.Note>
  );
};

export default TopicNoteComponent;
